// Supporter-only monthly playlists page.
//
// Free users see a paywall explaining the perk. Paid users see a list of
// published playlists with track listings and in-page audio playback.

import { createFileRoute } from '@tanstack/react-router';
import { Lock, Music } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '~/lib/api-client';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';
import { ProducerHandle } from '~/ui/ProducerHandle';
import { Badge } from '~/ui/components/badge';
import { Button } from '~/ui/components/button';
import { Card, CardContent, CardHeader } from '~/ui/components/card';

export const Route = createFileRoute('/supporter/playlists')({
  head: () =>
    buildHead({
      title: 'Supporter Playlists',
      description: 'Monthly curated playlists of battle winners. Supporter perk.',
      path: '/supporter/playlists',
      noindex: true,
    }),
  component: SupporterPlaylists,
});

type PlaylistTrack = {
  id: string;
  title: string | null;
  audioUrl: string;
  waveformUrl: string | null;
  score: number;
  finalRank: number | null;
  producerHandle: string;
  isSupporter: boolean;
  genreSlug: string;
  createdAt: string;
};

type PlaylistDetail = {
  id: string;
  month: string;
  title: string;
  description: string | null;
  curatorHandle: string;
  tracks: PlaylistTrack[];
  publishedAt: string;
};

type PlaylistSummary = {
  id: string;
  month: string;
  title: string;
  description: string | null;
  trackCount: number;
  publishedAt: string;
};

function formatMonth(iso: string): string {
  const [year, month] = iso.split('-');
  const d = new Date(Number(year), Number(month) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function TrackRow({ track }: { track: PlaylistTrack }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  return (
    <div className="space-y-1 rounded-md border border-border bg-card p-3">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        {track.finalRank != null && (
          <span className="font-mono text-xs text-muted-foreground">#{track.finalRank}</span>
        )}
        <span className="font-medium">{track.title ?? 'Untitled'}</span>
        <ProducerHandle
          handle={track.producerHandle}
          isSupporter={track.isSupporter}
          className="text-xs text-muted-foreground"
        />
        <Badge variant="secondary" className="capitalize text-[11px]">
          {track.genreSlug}
        </Badge>
        <span className="ml-auto font-mono text-xs text-muted-foreground tabular-nums">
          {track.score.toFixed(1)} pts
        </span>
      </div>
      {/* biome-ignore lint/a11y/useMediaCaption: user-submitted audio */}
      <audio ref={audioRef} src={track.audioUrl} controls className="w-full h-8" preload="none" />
    </div>
  );
}

function PlaylistCard({
  summary,
  onExpand,
}: {
  summary: PlaylistSummary;
  onExpand: (month: string) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold">{summary.title}</h3>
            <p className="text-sm text-muted-foreground">{formatMonth(summary.month)}</p>
          </div>
          <Badge variant="outline" className="shrink-0">
            {summary.trackCount} tracks
          </Badge>
        </div>
        {summary.description && (
          <p className="text-sm text-muted-foreground">{summary.description}</p>
        )}
      </CardHeader>
      <CardContent>
        <Button variant="outline" onClick={() => onExpand(summary.month)}>
          Listen
        </Button>
      </CardContent>
    </Card>
  );
}

function PlaylistDetailView({ month, onClose }: { month: string; onClose: () => void }) {
  const [detail, setDetail] = useState<PlaylistDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api<PlaylistDetail>(`/supporter/playlists/${month}`)
      .then(setDetail)
      .catch((e) => setErr((e as Error).message));
  }, [month]);

  if (err) {
    return (
      <div className="space-y-3">
        <Button variant="outline" onClick={onClose}>
          Back
        </Button>
        <p className="text-destructive text-sm">{err}</p>
      </div>
    );
  }

  if (!detail) {
    return <p className="text-muted-foreground">Loading...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={onClose}>
          Back
        </Button>
        <div>
          <h2 className="text-2xl font-bold">{detail.title}</h2>
          <p className="text-sm text-muted-foreground">
            {formatMonth(detail.month)} - curated by @{detail.curatorHandle}
          </p>
        </div>
      </div>
      {detail.description && <p className="text-muted-foreground">{detail.description}</p>}
      <div className="space-y-2">
        {detail.tracks.map((t) => (
          <TrackRow key={t.id} track={t} />
        ))}
        {detail.tracks.length === 0 && (
          <p className="text-muted-foreground text-sm">No tracks in this playlist yet.</p>
        )}
      </div>
    </div>
  );
}

function SupporterPlaylists() {
  const { data: session, isLoading } = useSession();
  const [playlists, setPlaylists] = useState<PlaylistSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [activeMonth, setActiveMonth] = useState<string | null>(null);

  const isSupporter = session?.plan === 'paid';
  const isAdmin = session?.role === 'admin';
  const canAccess = isSupporter || isAdmin;

  useEffect(() => {
    if (!canAccess) return;
    api<{ items: PlaylistSummary[] }>('/supporter/playlists')
      .then((r) => setPlaylists(r.items))
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 302) {
          // Shouldn't happen - API sends 302 redirect which fetch follows,
          // but canAccess gate above already handles non-supporters.
          setErr('Supporter access required.');
        } else {
          setErr((e as Error).message);
        }
      });
  }, [canAccess]);

  if (isLoading) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12">
        <p className="text-muted-foreground">Loading...</p>
      </section>
    );
  }

  // Paywall for free / unauthed users.
  if (!canAccess) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-20 text-center space-y-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-primary/10 p-4">
            <Lock className="size-8 text-primary" />
          </div>
        </div>
        <h2 className="text-3xl font-bold">Supporter only</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          Monthly playlists are hand-curated by the Prod Battle A&R team and exclusively available
          to supporters. Upgrade for €2,95/mo to unlock this and other perks.
        </p>
        <div className="flex justify-center gap-3">
          <Button asChild>
            <a href="/pricing">Become a supporter</a>
          </Button>
          <Button variant="outline" asChild>
            <a href="/feed">Browse the feed</a>
          </Button>
        </div>
      </section>
    );
  }

  if (activeMonth) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12">
        <PlaylistDetailView month={activeMonth} onClose={() => setActiveMonth(null)} />
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 space-y-8">
      <header className="space-y-1">
        <h2 className="text-3xl font-bold flex items-center gap-2">
          <Music className="size-6 text-primary" aria-hidden />
          Monthly Playlists
        </h2>
        <p className="text-muted-foreground text-sm">
          A curated selection of standout tracks from each month's battles.
        </p>
      </header>

      {err && <p className="text-destructive text-sm">{err}</p>}

      {playlists === null ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : playlists.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground">
          No playlists yet. Check back next month.
        </div>
      ) : (
        <div className="space-y-4">
          {playlists.map((p) => (
            <PlaylistCard key={p.id} summary={p} onExpand={setActiveMonth} />
          ))}
        </div>
      )}
    </section>
  );
}
